import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LogOut, Home, User, CheckCircle, Clock, FileText, Phone, Building, Eye, Printer } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function CustomerPortal({ customer, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewBooking, setViewBooking] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    fetchMyBookings();
  }, []);

  const fetchMyBookings = async () => {
    try {
      const response = await api.get('/customer-portal/my-bookings');
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load your bookings');
    } finally {
      setLoading(false);
    }
  };

  const totalPaid = bookings.reduce((sum, b) => sum + b.total_paid, 0);
  const totalPending = bookings.reduce((sum, b) => sum + b.balance_due, 0);
  const totalBookings = bookings.length;

  const getStatusBadge = (status) => {
    if (status === 'Fully Paid') {
      return <Badge className="bg-green-100 text-green-700">Fully Paid</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700">Partially Paid</Badge>;
  };

  const getInstallmentStatusBadge = (status) => {
    if (status === 'Paid') {
      return <Badge className="bg-green-100 text-green-700 gap-1"><CheckCircle size={12} /> Paid</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 gap-1"><Clock size={12} /> Pending</Badge>;
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice - ${viewBooking?.booking_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; color: #1e293b; }
            .header { border-bottom: 3px solid #059669; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { color: #059669; margin: 0; font-size: 28px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 12px; }
            td { padding: 12px; border-bottom: 1px solid #e2e8f0; }
            .status-paid { color: #059669; font-weight: 600; }
            .status-pending { color: #d97706; font-weight: 600; }
            .footer { border-top: 2px solid #e2e8f0; padding-top: 20px; margin-top: 40px; text-align: center; color: #64748b; font-size: 12px; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Building className="text-white" size={20} />
            </div>
            <div>
              <h1 className="font-bold text-slate-800">Ganesa Homestate</h1>
              <p className="text-xs text-slate-500">Customer Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="font-medium text-slate-800">{customer?.name}</p>
              <p className="text-xs text-slate-500 font-mono">{customer?.login_id}</p>
            </div>
            <Button variant="outline" size="sm" onClick={onLogout} className="gap-2">
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8" data-testid="customer-portal">
        {/* Welcome Card */}
        <Card className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white border-0">
          <CardContent className="py-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <User size={32} />
              </div>
              <div>
                <p className="text-emerald-100">Welcome back,</p>
                <h2 className="text-2xl font-bold">{customer?.name}</h2>
                <p className="text-emerald-200 text-sm mt-1 font-mono">ID: {customer?.login_id}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="stat-total-paid">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">₹{totalPaid.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-xl">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Amount</p>
                  <p className="text-2xl font-bold text-amber-600">₹{totalPending.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Clock className="text-amber-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-bookings">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Bookings</p>
                  <p className="text-2xl font-bold text-slate-800">{totalBookings}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-xl">
                  <Home className="text-slate-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="text-emerald-600" size={20} />
              My Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bookings.length === 0 ? (
              <div className="text-center py-12">
                <Home size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-lg font-medium text-slate-600">No bookings yet</p>
                <p className="text-slate-500">Your property bookings will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="p-4 border border-slate-200 rounded-xl hover:border-emerald-300 hover:shadow-md transition-all"
                    data-testid={`booking-card-${booking.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-emerald-100 rounded-xl">
                          <Home className="text-emerald-600" size={24} />
                        </div>
                        <div>
                          <p className="font-mono text-sm text-slate-500">{booking.booking_number}</p>
                          <h3 className="font-semibold text-lg text-slate-800">{booking.property_name}</h3>
                          <p className="text-sm text-slate-500">{booking.property_size}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Total Price</p>
                          <p className="text-xl font-bold text-slate-800">₹{booking.total_price.toLocaleString('en-IN')}</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {getStatusBadge(booking.payment_status)}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewBooking(booking)}
                            className="gap-1"
                          >
                            <Eye size={14} />
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                      <div>
                        <p className="text-xs text-slate-500">Payment Type</p>
                        <p className="font-medium text-slate-700">{booking.payment_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Paid</p>
                        <p className="font-medium text-green-600">₹{booking.total_paid.toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Balance</p>
                        <p className="font-medium text-red-600">₹{booking.balance_due.toLocaleString('en-IN')}</p>
                      </div>
                    </div>

                    {/* Upcoming Installment */}
                    {booking.payment_type === 'Installment' && booking.installments && (
                      <div className="mt-4">
                        {booking.installments.filter(i => i.status === 'Pending').slice(0, 1).map((inst, idx) => (
                          <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-xs text-amber-600 font-medium">Next Payment Due</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="font-medium text-slate-800">{inst.installment_name}</span>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-amber-700">₹{inst.amount.toLocaleString('en-IN')}</span>
                                <span className="text-xs text-slate-500">
                                  Due: {new Date(inst.due_date).toLocaleDateString('en-IN')}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-xl border border-slate-200">
                <Phone className="text-emerald-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Need Help?</h3>
                <p className="text-sm text-slate-500">Contact our support team for any queries regarding your bookings</p>
                <p className="text-emerald-600 font-medium mt-1">support@ganesarealestate.com</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Booking Detail Dialog */}
      <Dialog open={!!viewBooking} onOpenChange={(open) => !open && setViewBooking(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Booking Details</DialogTitle>
              <Button onClick={handlePrint} size="sm" className="gap-2">
                <Printer size={16} />
                Print
              </Button>
            </div>
          </DialogHeader>
          
          {viewBooking && (
            <div ref={printRef} className="space-y-6">
              {/* Header */}
              <div className="border-b-4 border-emerald-600 pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-emerald-800">Ganesa Real Estate</h1>
                    <p className="text-slate-600 text-sm">Property Booking Invoice</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-xl font-bold text-slate-800">INVOICE</h2>
                    <p className="text-slate-600 font-mono">{viewBooking.booking_number}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Date: {new Date(viewBooking.created_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Property & Customer Details */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Property Details</p>
                  <div className="space-y-1">
                    <p className="font-bold text-lg text-slate-800">{viewBooking.property_name}</p>
                    <p className="text-slate-600">Size: {viewBooking.property_size}</p>
                    <p className="text-emerald-600 font-bold text-xl">
                      Total: ₹{viewBooking.total_price.toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase">Payment Summary</p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Payment Type:</span>
                      <span className="font-medium">{viewBooking.payment_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Paid:</span>
                      <span className="font-medium text-green-600">₹{viewBooking.total_paid.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Balance:</span>
                      <span className="font-medium text-red-600">₹{viewBooking.balance_due.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Installment Schedule */}
              {viewBooking.installments && viewBooking.installments.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Installment Schedule</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Installment</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewBooking.installments.map((inst, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{inst.installment_name}</TableCell>
                          <TableCell className="font-semibold">₹{inst.amount.toLocaleString('en-IN')}</TableCell>
                          <TableCell>{new Date(inst.due_date).toLocaleDateString('en-IN')}</TableCell>
                          <TableCell>{getInstallmentStatusBadge(inst.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Full Payment Status */}
              {viewBooking.payment_type === 'Full Payment' && (
                <div className="p-6 bg-green-50 rounded-xl text-center">
                  <CheckCircle size={48} className="mx-auto text-green-600 mb-3" />
                  <p className="text-xl font-bold text-green-700">Fully Paid</p>
                  <p className="text-green-600">Complete payment received</p>
                </div>
              )}

              {/* Footer */}
              <div className="border-t-2 border-slate-200 pt-4 text-center text-slate-500 text-sm">
                <p>Thank you for choosing Ganesa Real Estate!</p>
                <p className="mt-1">For queries: support@ganesarealestate.com</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
